import { observeIntersection } from '@/shared/lib';
import { HTMLAttributes, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

export type ExposeProps = PropsWithChildren<
  {
    targetRef?: React.RefObject<HTMLDivElement>;
    defaultExposed?: boolean;
    skeletonWidth?: number | string;
    skeletonHeight?: number | string;
    skeletonRatio?: string;
    threshold?: number;
    exposeDuration?: number;
    hiddenDuration?: number;
    root?: Element | Document | null;
    rootMargin?: string;
    optimize?: boolean;
    onExposed?: () => void;
    onHidden?: () => void;
  } & IntersectionObserverInit &
    HTMLAttributes<HTMLDivElement>
>;

export const Expose = ({
  targetRef,
  defaultExposed = true,
  skeletonWidth,
  skeletonHeight,
  skeletonRatio,
  threshold = 1,
  exposeDuration = 0,
  hiddenDuration = 0,
  root,
  rootMargin,
  children,
  optimize,
  onExposed,
  onHidden,
  ...props
}: ExposeProps) => {
  const localRef = useRef<HTMLDivElement>(null);
  const currentRef = useMemo(() => targetRef || localRef, [targetRef]);
  const [exposed, setExposed] = useState(defaultExposed);
  const [style, setStyle] = useState<React.CSSProperties | undefined>(
    skeletonWidth || skeletonHeight ? { width: skeletonWidth, height: skeletonHeight } : undefined,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const current = currentRef.current;

    if (!current) {
      return;
    }

    const schedule = (duration: number, run: () => void) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(run, duration);
    };

    const expose = () => {
      setStyle(undefined);
      setExposed(true);
    };

    const hide = () => {
      setStyle({
        width: current.clientWidth || skeletonWidth,
        height: current.clientHeight || skeletonHeight,
        ...(skeletonRatio ? { aspectRatio: skeletonRatio } : {}),
      });
      setExposed(false);
    };

    const unobserve = observeIntersection({
      target: current,
      root,
      threshold,
      rootMargin,
      callback: (entry) => {
        if (entry.isIntersecting || entry.intersectionRatio >= threshold) {
          if (onExposed) {
            onExposed();
          }

          if (exposeDuration) {
            schedule(exposeDuration, expose);
          } else {
            expose();
          }

          return;
        }

        if (onHidden) {
          onHidden();
        }

        // 아래에서 고정하는 크기는 optimize 로 자식을 언마운트할 때 자리를 잡아두기 위한 것이다.
        // 자식이 그대로 남아 있으면 고정할 이유가 없고, 오히려 레이아웃을 어긋나게 만든다.
        //  - clientHeight 는 정수로 반올림되므로 소수점 높이(예: text-[32px] 헤더의 38.4px)가 깎인다.
        //  - height 가 auto 가 아니게 되면서, 마지막 자식의 아래 margin(mb-[16px]) 이
        //    부모 밖으로 병합되지 않고 안쪽으로 들어간다. → 뒤따르는 줄의 위치가 밀린다.
        // 스크롤 중 줄이 화면을 드나들 때마다 이 어긋남이 반복되어 화면이 떨린다.
        if (!optimize) {
          return;
        }

        if (hiddenDuration) {
          schedule(hiddenDuration, hide);
        } else {
          hide();
        }
      },
    });

    return () => {
      unobserve();

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, [
    currentRef,
    exposeDuration,
    hiddenDuration,
    onExposed,
    onHidden,
    optimize,
    root,
    rootMargin,
    skeletonHeight,
    skeletonRatio,
    skeletonWidth,
    threshold,
  ]);

  return (
    <div ref={currentRef} style={style} {...props}>
      {(exposed || !optimize) && children}
    </div>
  );
};
